import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, ArrowRight, Facebook, Instagram, Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import SectionHeader from "@/components/SectionHeader";
import SuccessCard from "@/components/SuccessCard";
import { useI18n } from "@/i18n";
import { api } from "@/lib/api";
import { company, social } from "@/data/brand";
import { services } from "@/data/services";
import { MessengerIcon, WhatsAppIcon } from "@/components/FloatingCTA";

const initial = {
  name: "", phone: "", email: "", vehicle: "", engine: "",
  registration: "", service: "", message: "", hp: "",
};

export default function Contact() {
  const { t, lang } = useI18n();
  const [f, setF] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fields = t("contact.form.fields");
  const hours = t("contact.hours");

  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (loading || submitted) return;
    if (f.hp) return; // client-side honeypot short-circuit
    setLoading(true);
    try {
      await api.post("/contact", { ...f, language: lang });
      setF(initial);
      setSubmitted(true);
    } catch {
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => setSubmitted(false);

  return (
    <Layout seoTitle={t("contact.title")} seoDescription={t("contact.subtitle")}>
      <section className="section-y">
        <div className="container-mt">
          <SectionHeader as="h1" title={t("contact.title")} subtitle={t("contact.subtitle")} />

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
            {/* Info column */}
            <div className="lg:col-span-2 space-y-4">
              <div className="card-mt card-mt-pad">
                <div className="flex items-start gap-4">
                  <div className="icon-tile !w-10 !h-10"><MapPin size={16} /></div>
                  <div className="flex-1">
                    <p className="eyebrow mb-1">{t("contact.addressTitle")}</p>
                    <p className="text-white font-medium">{lang === "lt" ? company.location.lt : company.location.en}</p>
                  </div>
                </div>
              </div>
              <div className="card-mt card-mt-pad">
                <div className="flex items-start gap-4">
                  <div className="icon-tile !w-10 !h-10"><Phone size={16} /></div>
                  <div className="flex-1">
                    <p className="eyebrow mb-1">{t("contact.phoneLabel")}</p>
                    <a href={`tel:${company.phoneRaw}`} className="text-white font-mono hover:text-brand-lime transition-colors">{company.phone}</a>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <a href={`tel:${company.phoneRaw}`} data-testid="contact-quick-call" aria-label="Skambinti MAD TURBO"
                         className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-white bg-brand-lime/10 hover:bg-brand-lime hover:text-black border border-brand-lime/40 hover:border-brand-lime rounded-full px-3 py-1.5 transition-colors duration-200">
                        <Phone size={12} /> {lang === "lt" ? "Skambinti" : "Call"}
                      </a>
                      <a href={social.whatsapp} target="_blank" rel="noreferrer noopener" data-testid="contact-quick-whatsapp"
                         aria-label="Rašyti WhatsApp"
                         className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-300 hover:text-brand-lime border border-zinc-800 hover:border-brand-lime rounded-full px-3 py-1.5 transition-colors duration-200">
                        <WhatsAppIcon size={12} /> WhatsApp
                      </a>
                      <a href={social.messenger} target="_blank" rel="noreferrer noopener" data-testid="contact-quick-messenger"
                         aria-label="Rašyti Messenger"
                         className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-300 hover:text-brand-lime border border-zinc-800 hover:border-brand-lime rounded-full px-3 py-1.5 transition-colors duration-200">
                        <MessengerIcon size={12} /> Messenger
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-mt card-mt-pad">
                <div className="flex items-start gap-4">
                  <div className="icon-tile !w-10 !h-10"><Mail size={16} /></div>
                  <div className="flex-1">
                    <p className="eyebrow mb-1">{t("contact.emailLabel")}</p>
                    <a href={`mailto:${company.email1}`} className="text-white block text-sm hover:text-brand-lime transition-colors">{company.email1}</a>
                    <a href={`mailto:${company.email2}`} className="text-white block text-sm hover:text-brand-lime transition-colors">{company.email2}</a>
                  </div>
                </div>
              </div>
              <div className="card-mt card-mt-pad">
                <div className="flex items-start gap-4">
                  <div className="icon-tile !w-10 !h-10"><Clock size={16} /></div>
                  <div className="flex-1">
                    <p className="eyebrow mb-3">{t("contact.hoursTitle")}</p>
                    <ul className="space-y-1.5 text-sm">
                      {hours.map((h, i) => (
                        <li key={i} className="flex justify-between text-zinc-300 gap-4">
                          <span>{h.d}</span>
                          <span className="font-mono text-brand-lime text-xs whitespace-nowrap">{h.h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              <div className="card-mt card-mt-pad">
                <p className="eyebrow mb-4">{t("labels.social")}</p>
                <div className="flex flex-wrap gap-2">
                  <a href={social.facebook} data-testid="contact-social-facebook" aria-label="Facebook" target="_blank" rel="noreferrer noopener" className="w-10 h-10 rounded-full border border-zinc-800 hover:border-brand-lime text-zinc-400 hover:text-brand-lime flex items-center justify-center transition-colors"><Facebook size={15} /></a>
                  <a href={social.instagram} data-testid="contact-social-instagram" aria-label="Instagram" target="_blank" rel="noreferrer noopener" className="w-10 h-10 rounded-full border border-zinc-800 hover:border-brand-lime text-zinc-400 hover:text-brand-lime flex items-center justify-center transition-colors"><Instagram size={15} /></a>
                  <a href={social.messenger} data-testid="contact-social-messenger" aria-label="Messenger" target="_blank" rel="noreferrer noopener" className="w-10 h-10 rounded-full border border-zinc-800 hover:border-brand-lime text-zinc-400 hover:text-brand-lime flex items-center justify-center transition-colors"><MessengerIcon size={15} /></a>
                  <a href={social.whatsapp} data-testid="contact-social-whatsapp" aria-label="WhatsApp" target="_blank" rel="noreferrer noopener" className="w-10 h-10 rounded-full border border-zinc-800 hover:border-brand-lime text-zinc-400 hover:text-brand-lime flex items-center justify-center transition-colors"><WhatsAppIcon size={15} /></a>
                </div>
              </div>
            </div>

            {/* Form column */}
            <div className="lg:col-span-3">
              <div className="card-mt card-mt-pad !p-6 md:!p-10">
                {submitted ? (
                  <div data-testid="contact-success">
                    <SuccessCard
                      testid="contact-success-card"
                      title={t("successCards.contactTitle")}
                      body={t("successCards.contactBody")}
                    />
                    <button
                      type="button"
                      onClick={reset}
                      data-testid="contact-submit-another"
                      className="btn-outline mt-6"
                    >
                      {t("common.submitAnother")}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mb-8">
                      <p className="kicker mb-3"><span className="kicker__bar" /><span>{t("labels.consultation")}</span></p>
                      <h2 className="h-display text-3xl md:text-4xl text-white">{t("contact.form.title")}</h2>
                    </div>
                    <form onSubmit={submit} data-testid="contact-form" noValidate className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                      {/* Honeypot — hidden from real users */}
                      <input
                        type="text"
                        name="website_url"
                        value={f.hp}
                        onChange={(e) => set("hp", e.target.value)}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
                      />
                      {[
                        ["name", true, "text"],
                        ["phone", true, "tel"],
                        ["email", true, "email"],
                        ["vehicle", false, "text"],
                        ["engine", false, "text"],
                        ["registration", false, "text"],
                      ].map(([k, req, type]) => (
                        <div key={k}>
                          <label htmlFor={`contact-${k}`} className="label-mt">{fields[k]}{req && " *"}</label>
                          <input id={`contact-${k}`} required={req} type={type} value={f[k]} onChange={(e) => set(k, e.target.value)} className="input-mt" data-testid={`contact-${k}`} autoComplete={type === "email" ? "email" : type === "tel" ? "tel" : "off"} />
                        </div>
                      ))}
                      <div className="md:col-span-2">
                        <label htmlFor="contact-service" className="label-mt">{fields.service}</label>
                        <select id="contact-service" value={f.service} onChange={(e) => set("service", e.target.value)} className="input-mt" data-testid="contact-service">
                          <option value="">—</option>
                          {services.map((s) => (
                            <option key={s.slug} value={s.slug}>{lang === "lt" ? s.title.lt : s.title.en}</option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label htmlFor="contact-message" className="label-mt">{fields.message} *</label>
                        <textarea id="contact-message" required rows={5} value={f.message} onChange={(e) => set("message", e.target.value)} className="input-mt resize-none" data-testid="contact-message" />
                      </div>
                      <div className="md:col-span-2 pt-2">
                        <button
                          type="submit"
                          disabled={loading}
                          data-testid="contact-submit"
                          aria-busy={loading}
                          className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {loading ? (<><Loader2 size={14} className="animate-spin" /> {t("common.submitting")}</>) : (<>{fields.submit} <ArrowRight size={14} /></>)}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>

              {/* Map — city-level marker for Šiauliai */}
              <div className="mt-6 rounded-2xl border border-zinc-800 overflow-hidden">
                <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between">
                  <p className="eyebrow">{t("labels.serviceArea")}</p>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{lang === "lt" ? company.location.lt : company.location.en}</p>
                </div>
                <iframe
                  title={`MAD TURBO — ${lang === "lt" ? company.location.lt : company.location.en}`}
                  src="https://www.google.com/maps?q=%C5%A0iauliai%20Lietuva&z=12&output=embed"
                  width="100%" height="320" style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(0.4)" }} loading="lazy"
                />
                <p className="px-5 py-3 border-t border-zinc-900 text-[11px] text-zinc-500 leading-relaxed">
                  {t("labels.cityLevelNote")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
